import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { App, Alert, Button, DatePicker, Descriptions, Flex, Select, Tag, Typography } from "antd";
import dayjs from "dayjs";
import { CompanyContext, UpdateCompanyContext } from "../../context/companyContext";
import { clientFetch } from "../../utils/clientFetch";
import {
  BackfillJob,
  BackfillProvider,
  isTerminalStatus,
  useBackfill,
} from "../../context/backfillContext";
import { useTranslation } from "react-i18next";

const API_BASE = `${process.env.REACT_APP_API_ENDPOINT}/apis/admin`;
const POLL_INTERVAL_MS = 3000;

type ActiveApiKey = { id: number; key_prefix: string; name: string };

function fmt(value: string | null | undefined): string {
  return value ? dayjs(value).format("YYYY-MM-DD HH:mm") : "-";
}

function fmtCount(value: number | null | undefined): number | string {
  return value ?? "-";
}

function BackfillPageInner() {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const { companyOptions } = useContext(CompanyContext);
  const { refreshCompanyOptions } = useContext(UpdateCompanyContext);
  const { startBackfill, getJob } = useBackfill();

  const [companyId, setCompanyId] = useState<number | null>(null);
  const [apiKeys, setApiKeys] = useState<ActiveApiKey[]>([]);
  const [apiKeyId, setApiKeyId] = useState<number | null>(null);
  const [from, setFrom] = useState<dayjs.Dayjs | null>(null);
  const [to, setTo] = useState<dayjs.Dayjs | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [job, setJob] = useState<BackfillJob | null>(null);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    refreshCompanyOptions();
  }, [refreshCompanyOptions]);

  // アクティブな対象 APIキーを取得 (selector 用)
  const loadActiveKeys = useCallback(async (cid: number) => {
    const q = new URLSearchParams({ companyId: String(cid), status: "active", page: "1", limit: "100" });
    const res = await clientFetch(`${API_BASE}/external-api-keys?${q.toString()}`, { method: "GET" });
    if (!res.ok) {
      setApiKeys([]);
      return;
    }
    const body = await res.json();
    const list = Array.isArray(body?.data) ? body.data : [];
    setApiKeys(
      list.map((k: { id: number; key_prefix: string; name: string }) => ({
        id: k.id,
        key_prefix: k.key_prefix,
        name: k.name,
      }))
    );
  }, []);

  // ポーリング停止
  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  // アンマウント時にポーリングを必ず止める
  useEffect(() => stopPolling, [stopPolling]);

  const onSelectCompany = useCallback(
    (cid: number) => {
      // 企業を切り替えたら旧企業ジョブのポーリングを止め、表示もリセットする
      // (旧 companyId/jobId を叩き続け、別企業の下に旧ジョブが残るのを防ぐ)
      stopPolling();
      setJob(null);
      setCompanyId(cid);
      setApiKeyId(null);
      loadActiveKeys(cid);
    },
    [loadActiveKeys, stopPolling]
  );

  // 進捗ポーリング開始 (終端状態で自動停止)
  const startPolling = useCallback(
    (cid: number, jobId: number) => {
      stopPolling();
      pollRef.current = setInterval(async () => {
        const result = await getJob(cid, jobId);
        if (!result.ok) return;
        setJob(result.job);
        if (isTerminalStatus(result.job.status)) {
          stopPolling();
        }
      }, POLL_INTERVAL_MS);
    },
    [getJob, stopPolling]
  );

  const onSubmit = useCallback(async () => {
    if (companyId == null) {
      message.error(t("backfill.selectCompanyError"));
      return;
    }
    if (apiKeyId == null) {
      message.error(t("backfill.selectApiKeyError"));
      return;
    }
    setSubmitting(true);
    try {
      const result = await startBackfill(companyId, {
        api_key_id: apiKeyId,
        from: from ? from.toISOString() : null,
        to: to ? to.toISOString() : null,
      });
      if (!result.ok) {
        message.error(result.message);
        return;
      }
      message.success(t("backfill.submitted"));
      setJob(result.job);
      if (!isTerminalStatus(result.job.status)) {
        startPolling(companyId, result.job.id);
      }
    } finally {
      setSubmitting(false);
    }
  }, [companyId, apiKeyId, from, to, startBackfill, message, startPolling, t]);

  return (
    <div style={{ padding: 24 }}>
      <Typography.Title level={3}>{t("backfill.title")}</Typography.Title>
      <Typography.Paragraph type="secondary">
        {t("backfill.description")}
      </Typography.Paragraph>

      <Flex vertical gap="middle" style={{ maxWidth: 520, marginBottom: 24 }}>
        <div>
          <div style={{ marginBottom: 4 }}>{t("backfill.labels.company")}</div>
          <Select
            showSearch
            placeholder={t("company.selectCompany")}
            style={{ width: "100%" }}
            optionFilterProp="label"
            value={companyId ?? undefined}
            onChange={onSelectCompany}
            options={companyOptions.map((c) => ({ label: c.company_name, value: c.id }))}
          />
        </div>
        <div>
          <div style={{ marginBottom: 4 }}>{t("backfill.labels.apiKey")}</div>
          <Select
            placeholder={companyId == null ? t("backfill.placeholders.selectCompanyFirst") : t("backfill.placeholders.selectApiKey")}
            style={{ width: "100%" }}
            disabled={companyId == null}
            value={apiKeyId ?? undefined}
            onChange={(v) => setApiKeyId(v)}
            options={apiKeys.map((k) => ({ label: `${k.name} (${k.key_prefix})`, value: k.id }))}
          />
        </div>
        <div>
          <div style={{ marginBottom: 4 }}>{t("backfill.labels.from")}</div>
          <DatePicker showTime style={{ width: "100%" }} value={from} onChange={setFrom} placeholder={t("backfill.placeholders.startDateTime")} />
        </div>
        <div>
          <div style={{ marginBottom: 4 }}>{t("backfill.labels.to")}</div>
          <DatePicker showTime style={{ width: "100%" }} value={to} onChange={setTo} placeholder={t("backfill.placeholders.endDateTime")} />
        </div>

        <Alert
          type="info"
          showIcon
          message={t("backfill.unsupportedNotice")}
        />

        <Button type="primary" loading={submitting} onClick={onSubmit}>
          {t("backfill.submit")}
        </Button>
      </Flex>

      {job && (
        <div style={{ maxWidth: 520 }}>
          <Typography.Title level={5}>{t("backfill.jobTitle")}</Typography.Title>
          <Descriptions bordered size="small" column={1}>
            <Descriptions.Item label={t("backfill.job.id")}>{job.id}</Descriptions.Item>
            <Descriptions.Item label={t("common.status")}>
              <Tag color={isTerminalStatus(job.status) ? "blue" : "processing"}>{job.status}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label={t("backfill.job.total")}>{fmtCount(job.total_count)}</Descriptions.Item>
            <Descriptions.Item label={t("backfill.job.enqueued")}>{fmtCount(job.enqueued_count)}</Descriptions.Item>
            <Descriptions.Item label={t("backfill.job.error")}>{job.error ?? "-"}</Descriptions.Item>
            <Descriptions.Item label={t("backfill.job.createdAt")}>{fmt(job.created_at)}</Descriptions.Item>
          </Descriptions>
          {!isTerminalStatus(job.status) && (
            <Typography.Paragraph type="secondary" style={{ marginTop: 8 }}>
              {t("backfill.pollingMessage", { seconds: POLL_INTERVAL_MS / 1000 })}
            </Typography.Paragraph>
          )}
        </div>
      )}
    </div>
  );
}

export default function BackfillPage() {
  return (
    <BackfillProvider>
      <BackfillPageInner />
    </BackfillProvider>
  );
}
